import React, { useState, useMemo } from 'react';
import ImageLightbox from './ImageLightbox';
import SmartProductImage from './SmartProductImage';
import NumberPreviewThumb, { getPreviewFor, getPrintedNumberAttr } from './NumberPreviewThumb';
import {
    EMBELLISHMENT_TYPES,
    getGroupPlayerName,
    getGroupPlayerNumber,
    getLineItemRecipe,
    handleMissingImage,
    handleWhiteBgDetection,
    MISSING_IMAGE_SVG,
} from './pressingHelpers';
import { useT } from './i18n/I18nContext';
import { getPreviewImageUrls, shopifyResize } from './previewImage';

// Full-screen detail overlay shown when a bundle is in the Pressing column
// and the user taps it. Replaces the old ItemCycler for this single bundle.
//
// Props:
//   orderName:    string — e.g. "SURF-58306"
//   orderClub?:   string — club / team name (from order attrs)
//   group:        { gid, items, groupData }
//   bundleState?: { embPickStates: [{ isUnpicked, embSku, embIdx, lineItem }], ... }
//                 — per-bundle pick state, used to flag unpicked embellishment rows
//   onBack:       () => void — dismiss overlay only (bundle stays in Pressing)
//   onClose:      () => void — exit the entire portal (handleBack from parent)
//   onDone:       () => void — fires bundle_finish + moves card to Pressed

export default function BundleDetail({ orderName, orderClub, group, bundleState, onBack, onClose, onDone, numberPreviews = {}, tpOverridesByName = null, skuNotifications = {} }) {
    const t = useT();
    const [lightboxOpen, setLightboxOpen]     = useState(false);
    const [lightboxKey, setLightboxKey]       = useState('front');
    const [lightboxImages, setLightboxImages] = useState([]);

    const parent = group.items.find(li => !EMBELLISHMENT_TYPES.has(li.productType)) || group.items[0];
    const playerName   = getGroupPlayerName(group);
    const rawPlayerNumber = getGroupPlayerNumber(group);
    // TP override wins over the order-embedded number. isTpOverride drives the
    // red styling below.
    const tpOverride = playerName && tpOverridesByName ? tpOverridesByName[playerName] : null;
    const isTpOverride = !!(tpOverride && String(tpOverride.override_number || '') !== String(rawPlayerNumber || ''));
    const playerNumber = isTpOverride ? tpOverride.override_number : rawPlayerNumber;
    const garmentTitle = parent?.title || t('Garment');
    const variantTitle = parent?.variant?.title && parent.variant.title !== 'Default Title'
        ? parent.variant.title : '';

    const allAttrsForPreview = [
        ...(group.groupData?.customAttributes || []),
        ...(parent?.customAttributes || []),
        ...((group.items || []).filter(li => li !== parent).flatMap(li => li?.customAttributes || [])),
    ];
    const { front: previewFront, back: previewBack } = getPreviewImageUrls(allAttrsForPreview);
    const fallbackImg  = parent?.image?.url;

    // Build only the slots we actually have images for. If neither preview
    // attribute is set, fall through to a single slot using the product image.
    const imgSlots = [];
    if (previewFront) imgSlots.push({ key: 'front', label: t('Front'),     src: shopifyResize(previewFront, 1200) });
    if (previewBack)  imgSlots.push({ key: 'back',  label: t('Back side'), src: shopifyResize(previewBack,  1200) });
    if (imgSlots.length === 0 && fallbackImg) {
        imgSlots.push({ key: 'image', label: '', src: fallbackImg });
    }
    if (imgSlots.length === 0) {
        imgSlots.push({ key: 'missing', label: '', src: MISSING_IMAGE_SVG });
    }

    const lightboxFrontBack = useMemo(
        () => imgSlots.map(s => ({ key: s.key, label: s.label || t('Image'), src: s.src })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [previewFront, previewBack, fallbackImg]
    );

    // Open the Front/Back lightbox on a specific face.
    const openFrontBackLightbox = (key) => {
        setLightboxImages(lightboxFrontBack);
        setLightboxKey(key);
        setLightboxOpen(true);
    };

    // Open a lightbox showing a single embellishment image (or the missing
    // placeholder if no image is available).
    const openEmbLightbox = (embLi) => {
        const src = embLi.image?.url || embLi.variant?.image?.url || MISSING_IMAGE_SVG;
        const label = embLi.productType || t('Item');
        setLightboxImages([{ key: 'emb', label, src }]);
        setLightboxKey('emb');
        setLightboxOpen(true);
    };

    const playerSummary = [playerName, playerNumber ? `#${playerNumber}` : '', orderClub]
        .filter(Boolean).join(' · ');

    return (
        <div className="ppd">
            <div className="ppd__hdr">
                <div className="ppd__hdr-left">
                    <button className="ppd__back-btn" onClick={onBack}>{t('← Back to Board')}</button>
                    <div className="ppd__hdr-title">
                        <span className="ppd__order">{orderName}</span>
                        {playerSummary && <span className="ppd__sub"> · {playerSummary}</span>}
                    </div>
                </div>
            </div>

            <div className="ppd__content">
                <div className="ppd__left">
                    <div className="ppd__player-block">
                        <div>
                            <div className="ppd__player-title">{t('Now Pressing')}</div>
                            <div className="ppd__player-name">{playerName || garmentTitle}</div>
                            <div className="ppd__player-garment">{garmentTitle}</div>
                            {variantTitle && (
                                <span className="ppd__player-variant">{variantTitle}</span>
                            )}
                            {parent?.sku && (
                                <span className="ppd__player-sku">
                                    {parent.sku}
                                </span>
                            )}
                            {parent?.sku && skuNotifications[parent.sku] && (
                                <span style={{
                                    display: 'inline-block',
                                    marginLeft: 10,
                                    padding: '6px 14px',
                                    borderRadius: 16,
                                    background: '#fde8e8',
                                    color: '#c00',
                                    fontWeight: 700,
                                    fontSize: '1.1em',
                                    border: '2px solid #fbb',
                                    verticalAlign: 'middle',
                                }}>
                                    {skuNotifications[parent.sku]}
                                </span>
                            )}
                        </div>
                        {playerNumber && (
                            <div
                                className="ppd__player-number"
                                style={isTpOverride ? { color: '#c1281c' } : undefined}
                                title={isTpOverride ? `Team Portal Number Override (updated ${tpOverride.tp_updated_at || ''})` : undefined}
                            >
                                {playerNumber}
                                {isTpOverride && (
                                    <div style={{ color: '#c1281c', fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', marginTop: 6 }}>
                                        ⚠ TP Override
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="ppd__imgs-wrap">
                        <div className={`ppd__imgs-grid${imgSlots.length === 1 ? ' ppd__imgs-grid--single' : ''}`}>
                            {imgSlots.map(s => (
                                <div key={s.key} className="ppd__img" onClick={() => openFrontBackLightbox(s.key)}>
                                    <img
                                        src={s.src}
                                        alt={s.label || ''}
                                        draggable={false}
                                        onLoad={handleWhiteBgDetection}
                                        onError={handleMissingImage}
                                    />
                                    {s.label && <span className="ppd__img-label">{s.label}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Anything in the bundle that isn't the parent garment is an
                    embellishment — using a strict productType set excluded
                    items where Shopify product configuration left productType
                    blank (e.g. SP-FC custom logos), so the operator only saw
                    the Number step and missed the two logo presses. */}
                <div className="ppd__press-seq">
                    <div className="ppd__press-seq-title">
                        {t('Embellishments')} · {group.items.filter(li => li.id !== parent?.id).length}
                    </div>
                    {group.items
                        .filter(li => li.id !== parent?.id)
                        .map((embLi, idx) => {
                            const type = (embLi.productType || '').toLowerCase();
                            const rowClass =
                                type === 'name'   ? 'ppd__row ppd__row--name' :
                                type === 'number' ? 'ppd__row ppd__row--number' :
                                'ppd__row ppd__row--logo';
                            // Attribute value = the human-readable customization (e.g. "BENNER", "26", "Hawaii Surf Crest")
                            const customAttr = (embLi.customAttributes || [])
                                .find(a => {
                                    const k = (a.key || '').toLowerCase();
                                    return k === 'name' || k === 'number' || k === 'logo' || k === 'value' || k === 'text';
                                });
                            const rawValue = customAttr?.value || embLi.title || embLi.productType;
                            // For Number embellishments, the customization value
                            // IS the digit(s) being pressed onto the jersey.
                            // If the TP override applies to this player, swap
                            // in the RDS number so QC / press see the new one.
                            // (name/logo/other embellishments are unaffected.)
                            const value = (type === 'number' && isTpOverride && playerNumber)
                                ? String(playerNumber)
                                : rawValue;
                            // Placement (if set as an attribute on the line item — optional)
                            const placementAttr = (embLi.customAttributes || [])
                                .find(a => (a.key || '').toLowerCase() === 'placement');
                            const placement = placementAttr?.value || embLi.productType || '';
                            const recipe  = getLineItemRecipe(embLi);
                            const thumbSrc = embLi.image?.url || embLi.variant?.image?.url || MISSING_IMAGE_SVG;
                            const embPickState = (bundleState?.embPickStates || [])
                                .find(e => e.lineItem?.id === embLi.id);
                            const isUnpicked = !!embPickState?.isUnpicked;

                            return (
                                <div key={embLi.id || idx} className={rowClass}>
                                    <div className="ppd__row-info">
                                        <span className="ppd__row-placement">{placement}</span>
                                        {isUnpicked && (
                                            <span className="ppd__row-unpicked">{t('Unpicked')}</span>
                                        )}
                                        <div className="ppd__row-value">{value}</div>
                                        {embLi.sku && <div className="ppd__row-sku">{embLi.sku}</div>}
                                        {embLi.variant?.title && embLi.variant.title !== 'Default Title' && (
                                            <div className="ppd__row-variant">{embLi.variant.title}</div>
                                        )}
                                        <div className="ppd__row-recipe">
                                            {recipe.temp && (
                                                <span className="ppd__chip"><span className="ppd__chip-lbl">{t('Temp')}</span><span className="ppd__chip-val">{recipe.temp}</span></span>
                                            )}
                                            {recipe.pressure && (
                                                <span className="ppd__chip"><span className="ppd__chip-lbl">{t('Pressure')}</span><span className="ppd__chip-val">{recipe.pressure}</span></span>
                                            )}
                                            {recipe.time && (
                                                <span className="ppd__chip"><span className="ppd__chip-lbl">{t('Time')}</span><span className="ppd__chip-val">{recipe.time}</span></span>
                                            )}
                                            {recipe.peel && (
                                                <span className="ppd__chip"><span className="ppd__chip-lbl">{t('Peel')}</span><span className="ppd__chip-val">{recipe.peel}</span></span>
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className="ppd__row-thumb"
                                        onClick={(e) => { e.stopPropagation(); openEmbLightbox(embLi); }}
                                    >
                                        {(() => {
                                            // Swap to NumberPreviewThumb when this embellishment's
                                            // product has the soccer.player_number_preview metaobject
                                            // and we have a Number to render. Falls through to
                                            // SmartProductImage otherwise.
                                            const preview = getPreviewFor(embLi, numberPreviews);
                                            const num = getPrintedNumberAttr(embLi.customAttributes)
                                                || getPrintedNumberAttr(group?.items?.[0]?.customAttributes)
                                                || preview?.defaultValue || '';
                                            if (preview && num) {
                                                return <NumberPreviewThumb preview={preview} number={num} size={88} borderColor="transparent" />;
                                            }
                                            return (
                                                <SmartProductImage
                                                    src={thumbSrc}
                                                    alt=""
                                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                />
                                            );
                                        })()}
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </div>

            <div className="ppd__done-wrap">
                <button className="ppd__done-btn" onClick={onDone}>
                    {t('✓ Done — Move to Pressed')}
                </button>
            </div>

            <ImageLightbox
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                images={lightboxImages}
                initialKey={lightboxKey}
            />
        </div>
    );
}
